// The `path: '*'` catch-all is the LAST route at the TOP level — OUTSIDE the guard —
// so an unknown URL renders `<NotFoundPage>` regardless of auth state (R-18.177). If it
// were nested under `<ProtectedRoute>`, a logged-out user hitting a bad URL would be
// bounced to /login instead of seeing a 404, and the 404 would only be reachable while
// authenticated.

import { Spin } from 'antd';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';

import type { RouteObject } from 'react-router-dom';

import { PanelRedirect } from '@/app/PanelRedirect';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { RequireAnyCapability, RequireCapability } from '@/app/RequireCapability';
import { AppLayout } from '@/shell/AppLayout';
import { GateLayout } from '@/shell/GateLayout';
import { MyWorkLayout } from '@/shell/MyWorkLayout';

// /login and the catch-all 404 render OUTSIDE <AppLayout>, so they don't sit under its
// Suspense boundary — each needs its own wrapper with a meaningful fallback
// (R-18.174/R-18.140), mirroring the AppLayout one.
function RouteLoading(): React.JSX.Element {
  const { t } = useTranslation();
  return <Spin description={t('app.loading')} />;
}

// Feature route components load through their feature's public-API barrel
// (`@/features/<feature>`) — the only legal cross-feature import surface
// (R-18.81/R-18.90/R-18.95, DEC-029). HomePage/NotFoundPage have no owning feature and
// stay app-level under `@/pages` (DEC-029).
const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })));
const BacklogChangelogPage = lazy(() =>
  import('@/pages/BacklogChangelogPage').then((m) => ({ default: m.BacklogChangelogPage })),
);
const InstructionsPage = lazy(() =>
  import('@/pages/InstructionsPage').then((m) => ({ default: m.InstructionsPage })),
);
const LoginPage = lazy(() => import('@/features/auth').then((m) => ({ default: m.LoginPage })));
const UsersPage = lazy(() => import('@/features/users').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('@/features/roles').then((m) => ({ default: m.RolesPage })));
const SettingsPage = lazy(() =>
  import('@/features/settings').then((m) => ({ default: m.SettingsPage })),
);
const CommunicationsAdminPage = lazy(() =>
  import('@/features/communications').then((m) => ({ default: m.CommunicationsAdminPage })),
);
const AuditPage = lazy(() => import('@/features/audit').then((m) => ({ default: m.AuditPage })));
const TelephonyAdminPage = lazy(() =>
  import('@/features/telephony').then((m) => ({ default: m.TelephonyAdminPage })),
);
const CallsJournalPage = lazy(() =>
  import('@/features/telephony').then((m) => ({ default: m.CallsJournalPage })),
);
const CallbacksPage = lazy(() =>
  import('@/features/telephony').then((m) => ({ default: m.CallbacksPage })),
);
const ErrorLogPage = lazy(() =>
  import('@/features/error-log').then((m) => ({ default: m.ErrorLogPage })),
);
const GeneratorsPage = lazy(() =>
  import('@/features/generators').then((m) => ({ default: m.GeneratorsPage })),
);
const LocationsPage = lazy(() =>
  import('@/features/locations').then((m) => ({ default: m.LocationsPage })),
);
const CompanyDetailsPage = lazy(() =>
  import('@/features/company-details').then((m) => ({ default: m.CompanyDetailsPage })),
);
const RentalLocationsPage = lazy(() =>
  import('@/features/rental-locations').then((m) => ({ default: m.RentalLocationsPage })),
);
const StoragePackagesPage = lazy(() =>
  import('@/features/storage-packages').then((m) => ({ default: m.StoragePackagesPage })),
);
const AdminScheduledJobsPage = lazy(() =>
  import('@/features/admin-scheduled-jobs').then((m) => ({ default: m.AdminScheduledJobsPage })),
);
const AdminUtilsPage = lazy(() =>
  import('@/features/admin-utils').then((m) => ({ default: m.AdminUtilsPage })),
);
const TestingPage = lazy(() =>
  import('@/features/testing').then((m) => ({ default: m.TestingPage })),
);
const ProfilePage = lazy(() =>
  import('@/features/profile').then((m) => ({ default: m.ProfilePage })),
);
const DocumentTemplatesPage = lazy(() =>
  import('@/features/document-templates').then((m) => ({ default: m.DocumentTemplatesPage })),
);
const DocumentsArchivePage = lazy(() =>
  import('@/features/documents').then((m) => ({ default: m.DocumentsArchivePage })),
);
const EntityDocumentsPage = lazy(() =>
  import('@/features/entity-documents').then((m) => ({ default: m.EntityDocumentsPage })),
);
const ComplectationItemsPage = lazy(() =>
  import('@/features/complectation').then((m) => ({ default: m.ComplectationItemsPage })),
);
const AdMappingsPage = lazy(() =>
  import('@/features/ad-mappings').then((m) => ({ default: m.AdMappingsPage })),
);
const CustomersPage = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomersPage })),
);
const CustomerCardPage = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomerCardPage })),
);
const CustomerWizardDrawer = lazy(() =>
  import('@/features/customers').then((m) => ({ default: m.CustomerWizardDrawer })),
);
const VehiclesPage = lazy(() =>
  import('@/features/vehicles').then((m) => ({ default: m.VehiclesPage })),
);
const VehicleCardPage = lazy(() =>
  import('@/features/vehicles').then((m) => ({ default: m.VehicleCardPage })),
);
const VehicleWizardDrawer = lazy(() =>
  import('@/features/vehicles').then((m) => ({ default: m.VehicleWizardDrawer })),
);
const OwnedVehiclesPage = lazy(() =>
  import('@/features/inventory').then((m) => ({ default: m.OwnedVehiclesPage })),
);
const IntakeCardPage = lazy(() =>
  import('@/features/inventory').then((m) => ({ default: m.IntakeCardPage })),
);
const RegisterPurchaseDrawer = lazy(() =>
  import('@/features/inventory').then((m) => ({ default: m.RegisterPurchaseDrawer })),
);
const SalesPage = lazy(() =>
  import('@/features/sales').then((m) => ({ default: m.SalesPage })),
);
const SaleCardPage = lazy(() =>
  import('@/features/sales').then((m) => ({ default: m.SaleCardPage })),
);
const OfferPreviewPage = lazy(() =>
  import('@/features/vehicle-offers').then((m) => ({ default: m.OfferPreviewPage })),
);
const StartSellingDrawer = lazy(() =>
  import('@/features/sales').then((m) => ({ default: m.StartSellingDrawer })),
);
const StoragePage = lazy(() =>
  import('@/features/storage').then((m) => ({ default: m.StorageListPage })),
);
const StorageCardPage = lazy(() =>
  import('@/features/storage').then((m) => ({ default: m.StorageCardPage })),
);
const StorageCreateDrawer = lazy(() =>
  import('@/features/storage').then((m) => ({ default: m.StorageCreateDrawer })),
);
const StorageVehiclesPage = lazy(() =>
  import('@/features/storage').then((m) => ({ default: m.StorageVehiclesPage })),
);
// The warehouse (API schema/173): parts, code search, stock documents, journal, address trees.
const PartsListPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.PartsListPage })));
const UsedPartsListPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.UsedPartsListPage })));
const DonorsPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.DonorsPage })));
const DonorFormPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.DonorFormPage })));
const RrrPanelPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.RrrPanelPage })));
const RrrOrderPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.RrrOrderPage })));
const PartFormPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.PartFormPage })));
const PartCardPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.PartCardPage })));
const CodeSearchPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.CodeSearchPage })));
const StockDocumentsPage = lazy(() =>
  import('@/features/warehouse').then((m) => ({ default: m.StockDocumentsPage })),
);
const StockDocumentPage = lazy(() =>
  import('@/features/warehouse').then((m) => ({ default: m.StockDocumentPage })),
);
const MovementsPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.MovementsPage })));
const WarehousesPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.WarehousesPage })));
const ServiceCatalogPage = lazy(() => import('@/features/service-setup').then((m) => ({ default: m.ServiceCatalogPage })));
const ServiceWorkTypesPage = lazy(() => import('@/features/service-setup').then((m) => ({ default: m.WorkTypesPage })));
const ServiceStaffPage = lazy(() => import('@/features/service-setup').then((m) => ({ default: m.StaffPage })));
const ServiceWorkplacesPage = lazy(() => import('@/features/service-setup').then((m) => ({ default: m.WorkplacesPage })));
const ServiceStatusesPage = lazy(() => import('@/features/service-setup').then((m) => ({ default: m.StatusesPage })));
const ServiceSettingsPage = lazy(() => import('@/features/service-setup').then((m) => ({ default: m.ServiceSettingsPage })));
const WorkOrdersListPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.WorkOrdersListPage })));
const WorkOrderCardPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.WorkOrderCardPage })));
const PlannerPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.PlannerPage })));
const UnclosedPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.UnclosedPage })));
const ServiceReportsPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.ServiceReportsPage })));
const ExitApprovalsPage = lazy(() => import('@/features/exit-approvals').then((m) => ({ default: m.ExitApprovalsPage })));
const MyDayPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.MyDayPage })));
const JobPage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.JobPage })));
const CapturePage = lazy(() => import('@/features/work-orders').then((m) => ({ default: m.CapturePage })));
const TireStorageListPage = lazy(() => import('@/features/tire-storage').then((m) => ({ default: m.TireStorageListPage })));
const TireSetCardPage = lazy(() => import('@/features/tire-storage').then((m) => ({ default: m.TireSetCardPage })));
const TireIntakeDrawer = lazy(() => import('@/features/tire-storage').then((m) => ({ default: m.TireIntakeDrawer })));
const TireNotificationsPage = lazy(() => import('@/features/tire-storage').then((m) => ({ default: m.TireNotificationsPage })));
const TirePreSeasonOfferPage = lazy(() => import('@/features/tire-storage').then((m) => ({ default: m.TirePreSeasonOfferPage })));
const AddressTreePage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.AddressTreePage })));
const NodeCardPage = lazy(() => import('@/features/warehouse').then((m) => ({ default: m.NodeCardPage })));
const ServiceStaysPage = lazy(() =>
  import('@/features/service-stays').then((m) => ({ default: m.ServiceStaysPage })),
);
const ServiceStayCardPage = lazy(() =>
  import('@/features/service-stays').then((m) => ({ default: m.ServiceStayCardPage })),
);
const ServiceStayCreateDrawer = lazy(() =>
  import('@/features/service-stays').then((m) => ({ default: m.ServiceStayCreateDrawer })),
);
const RentalAgreementsPage = lazy(() =>
  import('@/features/rental').then((m) => ({ default: m.RentalAgreementsPage })),
);
const RentalCalendarPage = lazy(() =>
  import('@/features/rental').then((m) => ({ default: m.RentalCalendarPage })),
);
const RentalCardPage = lazy(() =>
  import('@/features/rental').then((m) => ({ default: m.RentalCardPage })),
);
const RentalCreateDrawer = lazy(() =>
  import('@/features/rental').then((m) => ({ default: m.RentalCreateDrawer })),
);
const RentalFleetPage = lazy(() =>
  import('@/features/rental').then((m) => ({ default: m.RentalFleetPage })),
);
const StorageLocationsPage = lazy(() =>
  import('@/features/storage-locations').then((m) => ({ default: m.StorageLocationsListPage })),
);
const StorageLocationDetailPage = lazy(() =>
  import('@/features/storage-locations').then((m) => ({ default: m.StorageLocationDetailPage })),
);
const OutgoingInvoiceListPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.OutgoingInvoiceListPage })),
);
const IncomingInvoiceListPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.IncomingInvoiceListPage })),
);
const InvoiceCardPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.InvoiceCardPage })),
);
const EmployeeExpensesPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.EmployeeExpensesPage })),
);
const DraftInvoicesPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.DraftInvoicesPage })),
);
const InvoiceLettersPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.InvoiceLettersPage })),
);
const FinanceReportsPage = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.FinanceReportsPage })),
);
const IssueInvoiceDrawer = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.IssueInvoiceDrawer })),
);
const CaptureIncomingBillDrawer = lazy(() =>
  import('@/features/finance').then((m) => ({ default: m.CaptureIncomingBillDrawer })),
);
// The Tildes Jumis accounting export (jumis-integration-plan §3.12) — its OWN feature, not a corner
// of finance: separate capabilities, a separate queue, a separate vocabulary.
const JumisPage = lazy(() => import('@/features/jumis').then((m) => ({ default: m.JumisPage })));
const BankPaymentsPage = lazy(() => import('@/features/bankPayments').then((m) => ({ default: m.BankPaymentsPage })));
const WorkflowsPage = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.WorkflowsPage })),
);
const WorkflowDetailPage = lazy(() =>
  import('@/features/workflows').then((m) => ({ default: m.WorkflowDetailPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const PanelChoicePage = lazy(() =>
  import('@/pages/PanelChoicePage').then((m) => ({ default: m.PanelChoicePage })),
);
const GateHomePage = lazy(() =>
  import('@/features/gate-panel').then((m) => ({ default: m.GateHomePage })),
);
const GateRecordPage = lazy(() =>
  import('@/features/gate-panel').then((m) => ({ default: m.GateRecordPage })),
);
const GateActPage = lazy(() =>
  import('@/features/gate-panel').then((m) => ({ default: m.GateActPage })),
);

const PortalPage = lazy(() =>
  import('@/features/service-portal').then((m) => ({ default: m.PortalPage })),
);

export const routes: RouteObject[] = [
  // /login renders outside the shell layout (no sider) AND outside the guard — it is
  // the auth landing target.
  {
    path: '/login',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    // Protected route group (R-18.179): the guard redirects unauthenticated users to
    // /login before the layout renders. `<ProtectedRoute>` renders an `<Outlet>` so the
    // layout — and everything under it — is gated.
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // The index is wrapped by the panel gatekeeper (gate-panel plan, decision 5): a gate-only
          // user — or a device that chose the gate panel — is forwarded to /gate before the
          // dashboard renders; a both-panels user with no remembered choice gets /choose-panel.
          {
            index: true,
            element: (
              <PanelRedirect>
                <HomePage />
              </PanelRedirect>
            ),
          },
          // /backlog-changelog — shell-level informational page (no capability gate): the
          // product backlog + release changelog, visible to every authenticated user. URL
          // segments are kebab-case (R-18.175).
          { path: 'backlog-changelog', element: <BacklogChangelogPage /> },
          // /instructions — shell-level informational page (no capability gate): the illustrated
          // how-to guides for the deal flows, visible to every authenticated user. URL segments
          // are kebab-case (R-18.175).
          { path: 'instructions', element: <InstructionsPage /> },
          // /admin/users (M10) nests under the guarded shell. The dead nav link from M8
          // now resolves to the real screen. URL segments are kebab-case (R-18.175).
          { path: 'admin/users', element: <UsersPage /> },
          // /admin/roles (M11) — roles pane + RBAC matrix, also under the guarded shell.
          { path: 'admin/roles', element: <RolesPage /> },
          // /admin/settings (PROD-009) — application settings, under the guarded shell.
          { path: 'admin/settings', element: <SettingsPage /> },
          // /admin/communications (client 07.09.2026) — the read-only mail-transport state (the
          // credentials live in server env, never here) plus the editable default message bodies.
          { path: 'admin/communications', element: <CommunicationsAdminPage /> },
          // /admin/telephony — phone calls from LMT Zvanu pārvaldnieks (API schema/230): ZP employees and their users.
          { path: 'admin/telephony', element: <TelephonyAdminPage /> },
          // /communications/calls — the calls journal and their review (telephony.calls.read; plan 2026_10_31_lmt_calls §5.6).
          { path: 'communications/calls', element: <CallsJournalPage /> },
          // /communications/callbacks — missed calls to call back (plan 2026_10_31_lmt_calls §5.7a).
          { path: 'communications/callbacks', element: <CallbacksPage /> },
          // /admin/audit (PROD-010) — audit log viewer + export, under the guarded shell.
          { path: 'admin/audit', element: <AuditPage /> },
          // /admin/error-log — the persisted API-failure viewer (error_log, db/schema/94):
          // status, code, internal cause, and stack per failed response, gated on errors.read.
          { path: 'admin/error-log', element: <ErrorLogPage /> },
          // /admin/generators (S-number-generators) — number-generator admin + issued-number
          // ledger, under the guarded shell.
          { path: 'admin/generators', element: <GeneratorsPage /> },
          // /admin/locations (S-physical-locations) — physical-location registry, under the
          // guarded shell.
          { path: 'admin/locations', element: <LocationsPage /> },
          // /admin/company-details (S-company-details) — issuing-party (company) details registry,
          // under the guarded shell. URL segments are kebab-case (R-18.175).
          { path: 'admin/company-details', element: <CompanyDetailsPage /> },
          // /admin/pickup-locations (S-rental-pickup-locations) — rental pickup-location label
          // dictionary, under the guarded shell. URL segments are kebab-case (R-18.175).
          { path: 'admin/pickup-locations', element: <RentalLocationsPage /> },
          // /admin/storage-packages (VS-M2) — storage service-package dictionary, under the
          // guarded shell. URL segments are kebab-case (R-18.175).
          { path: 'admin/storage-packages', element: <StoragePackagesPage /> },
          // /admin/complectation (Д-33) — the checklist-position dictionary a deal may promise from,
          // under the guarded shell. URL segments are kebab-case (R-18.175).
          { path: 'admin/complectation', element: <ComplectationItemsPage /> },
          // /admin/jobs (S-admin-scheduled-jobs) — scheduled-jobs admin (schedule entries, run
          // history, trigger), under the guarded shell. URL segments are kebab-case (R-18.175).
          { path: 'admin/jobs', element: <AdminScheduledJobsPage /> },
          // /admin/utils (S-admin-utils) — utility services (customer migration, backup, ledger replay)
          // with dry-run validation + downloadable run reports, under the guarded shell.
          { path: 'admin/utils', element: <AdminUtilsPage /> },
          // /admin/testing — placeholder admin page, under the guarded shell. Empty for now:
          // it exists so the nav entry and the URL are in place.
          { path: 'admin/testing', element: <TestingPage /> },
          // /admin/ad-mappings (S-sales-ads §8.3) — the ad-mappings dictionary admin (category registry +
          // per-platform value codes + per-category sync config), under the guarded shell. URL segments
          // are kebab-case (R-18.175).
          { path: 'admin/ad-mappings', element: <AdMappingsPage /> },
          // /profile (S-profile-self-edit) — self-service own-profile + 2FA page, under the
          // guarded shell. No capability gate: every authenticated user may edit their OWN
          // profile (the header pill's edit-profile entry routes here).
          { path: 'profile', element: <ProfilePage /> },
          // /document-templates (S-doc-templates-frontend) — admin template list + constructor
          // drawer, under the guarded shell. URL segments are kebab-case (R-18.175).
          { path: 'document-templates', element: <DocumentTemplatesPage /> },
          // /entity-documents (S-entity-documents) — per-entity-type document-setup registry
          // (templated + uploadable types) + the mandatory-by-state matrix, under the guarded shell.
          // URL segments are kebab-case (R-18.175).
          { path: 'entity-documents', element: <EntityDocumentsPage /> },
          // /documents (S-doc-templates-frontend) — the filled-document archive list, under the
          // guarded shell.
          { path: 'documents', element: <DocumentsArchivePage /> },
          // /customers (S-customers-ui) — the customer list. The create wizard drawer renders OVER
          // the list via a nested route (the list page's <Outlet>), so /customers/create keeps the
          // list mounted behind the drawer. URL segments are kebab-case (R-18.175).
          {
            path: 'customers',
            element: <CustomersPage />,
            children: [{ path: 'create', element: <CustomerWizardDrawer mode="create" /> }],
          },
          // /customers/show/:id (S-customers-ui) — the customer card, a standalone route (not
          // nested under the list) so its full-page tabs own the viewport. Edit renders OVER the card
          // via its nested <Outlet> (/customers/show/:id/edit), so the drawer opens on the profile
          // and a cancel/save returns there — never to the list.
          {
            path: 'customers/show/:id',
            element: <CustomerCardPage />,
            children: [{ path: 'edit', element: <CustomerWizardDrawer mode="edit" /> }],
          },
          // /vehicles (S-vehicles-ui) — the vehicle list. The create wizard drawer renders OVER the
          // list via a nested route (the list page's <Outlet>), so /vehicles/create keeps the list
          // mounted behind the drawer. URL segments are kebab-case (R-18.175).
          {
            path: 'vehicles',
            element: <VehiclesPage />,
            children: [{ path: 'create', element: <VehicleWizardDrawer mode="create" /> }],
          },
          // /vehicles/show/:id (S-vehicles-ui) — the vehicle card (the ten tabs + agata timeline), a
          // standalone route (not nested under the list) so its full-page tabs own the viewport. Edit
          // renders OVER the card via its nested <Outlet> (/vehicles/show/:id/edit), so the drawer
          // opens on the card and a cancel/save returns there — never to the list.
          {
            path: 'vehicles/show/:id',
            element: <VehicleCardPage />,
            children: [{ path: 'edit', element: <VehicleWizardDrawer mode="edit" /> }],
          },
          // /inventory/owned-vehicles (S-inventory-owned-ui) — the URL-driven views (?view=in-stock |
          // ?view=in-delivery | ?view=purchasing) with the KPI strip. The register-purchase wizard renders OVER the page via
          // the nested <Outlet>, so /inventory/owned-vehicles/register-purchase keeps the page mounted
          // behind the drawer. URL segments are kebab-case (R-18.175).
          {
            path: 'inventory/owned-vehicles',
            element: <OwnedVehiclesPage />,
            children: [
              { path: 'register-purchase', element: <RegisterPurchaseDrawer /> },
            ],
          },
          // /inventory/purchasing/show/:id (S-inventory-owned-ui) — the purchase-intake card, a standalone
          // route (not nested under the page) so its overview owns the viewport.
          { path: 'inventory/purchasing/show/:id', element: <IntakeCardPage /> },
          // /sales (S-sales-ui) — the sales list with the four URL-driven status sub-views (?status=).
          // The Start-selling wizard renders OVER the list via the nested <Outlet>, so
          // /sales/start-selling keeps the list mounted behind the drawer. URL segments are kebab-case
          // (R-18.175).
          {
            path: 'sales',
            element: <SalesPage />,
            children: [{ path: 'start-selling', element: <StartSellingDrawer /> }],
          },
          // /sales/show/:id (S-sales-ui) — the sale card (the full tab set), a standalone route (not
          // nested under the list) so its tabs own the viewport.
          { path: 'sales/show/:id', element: <SaleCardPage /> },
          // /storage-intakes (S-storage-ui) — the storage agreements list with the four sub-views
          // (?view=). The create wizard renders OVER the list via the nested <Outlet>, so
          // /storage-intakes/create keeps the list mounted behind the drawer. URL segments are kebab-case
          // (R-18.175).
          // /storage-intakes/service-stays/create — the "Receive for service" drawer of the page's
          // Service stays tab (client 18.09.2026), nested for the same reason: the tab stays mounted
          // behind it, and closing returns to it with its query string.
          {
            path: 'storage-intakes',
            element: <StoragePage />,
            children: [
              { path: 'create', element: <StorageCreateDrawer /> },
              {
                path: 'service-stays/create',
                element: <ServiceStayCreateDrawer listPathname="/storage-intakes" />,
              },
            ],
          },
          // /storage-intakes/show/:id (S-storage-ui) — the storage agreement card (the full tab set), a
          // standalone route (not nested under the list) so its tabs own the viewport.
          { path: 'storage-intakes/show/:id', element: <StorageCardPage /> },
          // /storage-vehicles — the flat list of every car a live storage contract covers
          // (feedback 23.08, decision 8), filterable by presence: with us, or with its owner.
          { path: 'storage-vehicles', element: <StorageVehiclesPage /> },
          // /service-stays (service-stay plan M3) — the cars in the zone WITHOUT a storage contract
          // (?view=). The create drawer renders OVER the list via the nested <Outlet>, so
          // /service-stays/create keeps the list mounted behind it. Since 18.09.2026 this is the
          // page for the roles WITHOUT storage.read only; everyone else is redirected to the
          // agreements page's Service stays tab (the page decides, so a deep link still resolves).
          {
            path: 'service-stays',
            element: <ServiceStaysPage />,
            children: [{ path: 'create', element: <ServiceStayCreateDrawer /> }],
          },
          // The warehouse (API schema/173, LIAA Modulis 6). Pages gate themselves on warehouse.read /
          // warehouse.write; the menu entries answer to the same keys.
          { path: 'parts/new', element: <PartsListPage kind="new" /> },
          { path: 'parts/new/create', element: <PartFormPage /> },
          // W5 (schema/176–177): used parts, donor cars and the RRR.LT panel.
          { path: 'parts/used', element: <UsedPartsListPage /> },
          { path: 'parts/used/create', element: <PartFormPage kind="used" /> },
          { path: 'parts/donors', element: <DonorsPage /> },
          { path: 'parts/donors/new', element: <DonorFormPage /> },
          { path: 'parts/donors/:id', element: <DonorFormPage /> },
          { path: 'warehouse/rrr', element: <RrrPanelPage /> },
          { path: 'warehouse/rrr/orders/:id', element: <RrrOrderPage /> },
          { path: 'parts/show/:id', element: <PartCardPage /> },
          { path: 'parts/show/:id/edit', element: <PartFormPage /> },
          { path: 'parts/search', element: <CodeSearchPage /> },
          { path: 'warehouse/documents', element: <StockDocumentsPage /> },
          { path: 'warehouse/documents/new', element: <StockDocumentPage /> },
          { path: 'warehouse/documents/:id', element: <StockDocumentPage /> },
          { path: 'warehouse/movements', element: <MovementsPage /> },
          { path: 'warehouse/locations', element: <WarehousesPage /> },
          { path: 'warehouse/locations/:id', element: <AddressTreePage /> },
          { path: 'warehouse/nodes/:id', element: <NodeCardPage /> },
          // The service module's dictionaries (API schema/180, LIAA service plan S1), behind service.settings
          // like their menu entries.
          {
            element: <RequireCapability capability="service.settings" />,
            children: [
              { path: 'service/catalog', element: <ServiceCatalogPage /> },
              { path: 'service/work-types', element: <ServiceWorkTypesPage /> },
              { path: 'service/staff', element: <ServiceStaffPage /> },
              { path: 'service/workplaces', element: <ServiceWorkplacesPage /> },
              { path: 'service/statuses', element: <ServiceStatusesPage /> },
              { path: 'service/settings', element: <ServiceSettingsPage /> },
            ],
          },
          // Work orders (API schema/181–182, LIAA service plan S2): one's own (a performer), every order or
          // the editors, the warehouse issuing parts (S5, no prices) — the API answers 404 for an order the viewer
          // may not see (P1-13.3).
          {
            element: <RequireAnyCapability capabilities={['service.read', 'service.read.all', 'service.wo.write', 'warehouse.write']} />,
            children: [
              { path: 'work-orders', element: <WorkOrdersListPage /> },
              { path: 'work-orders/:id', element: <WorkOrderCardPage /> },
            ],
          },
          // The planner (S6, P1-7): whoever sees every order; the API decides who books.
          {
            element: <RequireAnyCapability capabilities={['service.read.all', 'service.wo.write', 'service.booking.write']} />,
            children: [{ path: 'service/planner', element: <PlannerPage /> }],
          },
          // The unclosed orders panel (S7.4, P1-9.10): whoever sees every order.
          {
            element: <RequireAnyCapability capabilities={['service.read.all']} />,
            children: [{ path: 'service/unclosed', element: <UnclosedPage /> }],
          },
          // The service reports (S10, P1-17): `service.reports` (the API decides each report by its key).
          {
            element: <RequireAnyCapability capabilities={['service.reports']} />,
            children: [{ path: 'service/reports', element: <ServiceReportsPage /> }],
          },
          // Exit approvals (S8.2, P1-11.3): whoever may override the exit block — the e-mail's link lands here. The page
          // checks the right itself, so a link opened without it says why instead of redirecting (the API enforces it).
          { path: 'exit-approvals', element: <ExitApprovalsPage /> },
          { path: 'exit-approvals/:id', element: <ExitApprovalsPage /> },
          // Tire storage (API schema/174–175, LIAA 6c): customer sets in custody, the intake drawer over
          // the list, the set card, the notification log and the pre-season offer. Pages gate themselves
          // on tire-storage.read / write; the menu entry answers to the same key.
          {
            path: 'tire-storage',
            element: <TireStorageListPage />,
            children: [{ path: 'create', element: <TireIntakeDrawer /> }],
          },
          { path: 'tire-storage/show/:id', element: <TireSetCardPage /> },
          { path: 'tire-storage/notifications', element: <TireNotificationsPage /> },
          { path: 'tire-storage/offer', element: <TirePreSeasonOfferPage /> },
          // /service-stays/show/:id — the stay card, a standalone route so its tabs own the viewport.
          { path: 'service-stays/show/:id', element: <ServiceStayCardPage /> },
          // /storage-locations (S-storage-ui) — the Physical Storage occupancy list (location cards).
          { path: 'storage-locations', element: <StorageLocationsPage /> },
          // /storage-locations/:id (S-storage-ui) — one location's occupancy grid, including company cars
          // parked without an agreement.
          { path: 'storage-locations/:id', element: <StorageLocationDetailPage /> },
          // /rental (rental plan M6) — the module root: the resource calendar (fleet rows × days),
          // its KPI tiles and the active-rentals table. `?range=` / `?anchor=` pin the window.
          { path: 'rental', element: <RentalCalendarPage /> },
          // /rental/agreements — the bookings list (?view=). The create wizard renders OVER the list via
          // the nested <Outlet>, so /rental/agreements/create keeps the list mounted behind the drawer.
          {
            path: 'rental/agreements',
            element: <RentalAgreementsPage />,
            children: [{ path: 'create', element: <RentalCreateDrawer /> }],
          },
          // /rental/agreements/show/:id — the booking card, a standalone route so its tabs own the viewport.
          { path: 'rental/agreements/show/:id', element: <RentalCardPage /> },
          // /rental/fleet — the cars in the rental pool: rates, availability, ownership.
          { path: 'rental/fleet', element: <RentalFleetPage /> },
          // /finance/outgoing (S-finance-invoices-ui) — the outgoing invoices list with the KPI strip. The
          // issue wizard renders OVER the list via the nested <Outlet>, so /finance/outgoing/issue keeps the
          // list mounted behind the drawer. URL segments are kebab-case (R-18.175).
          {
            path: 'finance/outgoing',
            element: <OutgoingInvoiceListPage />,
            children: [{ path: 'issue', element: <IssueInvoiceDrawer direction="outgoing" /> }],
          },
          // /finance/outgoing/show/:id (S-finance-invoices-ui) — the outgoing invoice card (the six-tab set),
          // a standalone route so its tabs own the viewport.
          { path: 'finance/outgoing/show/:id', element: <InvoiceCardPage direction="outgoing" /> },
          // /finance/incoming (S-finance-invoices-ui) — the incoming (supplier) invoices list; the capture
          // flow renders OVER the list via the nested <Outlet>. Capture is the one-screen incoming drawer
          // (old-CRM form shape), not the outgoing issue wizard.
          {
            path: 'finance/incoming',
            element: <IncomingInvoiceListPage />,
            children: [{ path: 'capture', element: <CaptureIncomingBillDrawer /> }],
          },
          // /finance/incoming/show/:id (S-finance-invoices-ui) — the incoming invoice card.
          { path: 'finance/incoming/show/:id', element: <InvoiceCardPage direction="incoming" /> },
          // /finance/drafts (schema/118) — the draft-invoice tray: accumulating drafts + the
          // unbilled pool behind them, reviewed and drafted from one surface.
          { path: 'finance/drafts', element: <DraftInvoicesPage /> },
          // /finance/letters - "Vēstules nosūtīšanai": the letters about invoices that wait for a person (plan 2026_11_03_reminder_send_queue).
          { path: 'finance/letters', element: <InvoiceLettersPage /> },
          // /finance/expenses (S-finance-invoices-ui) — the employee expenses queue + allocation modal.
          { path: 'finance/expenses', element: <EmployeeExpensesPage /> },
          // /finance/reports (S-finance-reports) — the templated report library (generate + poll +
          // download). NO export/approve-to-accounting trigger is surfaced here (PA-finance-reports.2).
          { path: 'finance/reports', element: <FinanceReportsPage /> },
          // /finance/jumis (jumis-integration-plan §3.12) — the accounting export's own screen: the
          // approval queue, the invoices with no export record, the decisions the integration could not
          // take, and which accounting base we are actually pointed at. Behind its own capability, so a
          // finance user without the Jumis keys never sees it.
          {
            element: <RequireCapability capability="finance.jumis.read" />,
            children: [{ path: 'finance/jumis', element: <JumisPage /> }],
          },
          // /finance/bank-payments (Jumis closing plan A5) — the bank payments read from Jumis that wait for the
          // bookkeeper: post / distribute, link to a recorded payment, refund, reject, acknowledge a change. Behind the
          // bank-import capability (accountant and admin).
          {
            element: <RequireCapability capability="finance.bank-import" />,
            children: [{ path: 'finance/bank-payments', element: <BankPaymentsPage /> }],
          },
          // /admin/workflows (S-admin-workflows) — read-only Admin → Workflows index, rendered live
          // from the engine's machine definitions. URL segments are kebab-case (R-18.175).
          { path: 'admin/workflows', element: <WorkflowsPage /> },
          // /admin/workflows/:key — one machine's read-only detail (diagram, transition table, guard
          // inventory, override policies); accepts a `?failing=<action>` deep-link highlight.
          { path: 'admin/workflows/:key', element: <WorkflowDetailPage /> },
        ],
      },
    ],
  },
  // /sales/:saleId/offers/:offerId/preview — a vehicle offer's HTML preview in its own browser tab (vehicle offer
  // plan §4.4 R12): OUTSIDE the app shell (the page is the document), INSIDE the auth guard.
  {
    path: '/sales/:saleId/offers/:offerId/preview',
    element: <ProtectedRoute />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<RouteLoading />}>
            <OfferPreviewPage />
          </Suspense>
        ),
      },
    ],
  },
  // /choose-panel — the one-time panel choice screen (gate-panel plan, decision 5): app-level,
  // OUTSIDE both shells (like /login) but INSIDE the auth guard. Reached via the post-login /
  // index panel routing when both panels are usable and the device has no remembered choice.
  {
    path: '/choose-panel',
    element: <ProtectedRoute />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<RouteLoading />}>
            <PanelChoicePage />
          </Suspense>
        ),
      },
    ],
  },
  // /gate/* — the mobile gate panel (gate-panel plan §D): its own shell NEXT TO AppLayout, behind
  // the SPA's first route-level capability gate (`gate.access`). The guard order is auth →
  // capability → layout, so an unauthenticated user still lands on /login with state.from.
  {
    path: '/gate',
    element: <ProtectedRoute />,
    children: [
      {
        element: <RequireCapability capability="gate.access" />,
        children: [
          {
            element: <GateLayout />,
            children: [
              { index: true, element: <GateHomePage /> },
              { path: 'records/:group/:id', element: <GateRecordPage /> },
              { path: 'records/:group/:id/act/:action', element: <GateActPage /> },
            ],
          },
        ],
      },
    ],
  },
  // /my-work/* — the performer's phone panel (LIAA service plan S3.2, P2-2): its own mobile shell next to
  // AppLayout, like the gate panel. Guard order auth → capability → layout.
  {
    path: '/my-work',
    element: <ProtectedRoute />,
    children: [
      {
        element: <RequireAnyCapability capabilities={['service.work.own', 'service.wo.write', 'service.time.manage']} />,
        children: [
          {
            element: <MyWorkLayout />,
            children: [
              { index: true, element: <MyDayPage /> },
              { path: ':orderId/capture', element: <CapturePage /> },
              { path: ':orderId/:lineId', element: <JobPage /> },
            ],
          },
        ],
      },
    ],
  },
  // /s — the client's page by link (LIAA service plan S4.3, P1-12.4): outside every guard and shell, no staff menu.
  // The token is the URL fragment; the page sends it in a header to `/public/service`.
  {
    path: '/s',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <PortalPage />
      </Suspense>
    ),
  },
  // Catch-all 404 (R-18.177) — LAST and OUTSIDE the guard so it renders for logged-out
  // visitors too, instead of bouncing them to /login.
  {
    path: '*',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];
